/**
 * Leads (W13): what the acquisition scanner found, before any IM exists.
 *
 * The scanner runs every three days and records listings that match the
 * criteria. Every new lead is approved as it is recorded, and the scan then
 * requests its IM; approving an older lead here starts the request job, which signs the Knightsbridge NDA, fills the
 * listing's enquiry form or emails the broker, and the IM that comes back by
 * email lands on the board as a deal through the existing Gmail ingest.
 *
 * The page is a review queue: "To review" is the default tab, each lead shows
 * enough to decide without opening the listing (figures, what it is, why it
 * matched, what to be wary of), and the decision is one click.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { AuthExpiredError } from '../api/ims.js';
import {
  AUTOMATABLE,
  decideLead,
  listLeads,
  listLeadScans,
  MECHANISM_LABELS,
  startScan,
  type Lead,
  type LeadScan,
  type LeadStatus,
} from '../api/leads.js';
import { useSession } from '../components/Session.js';

type Tab = 'review' | 'requested' | 'rejected' | 'all';

const TABS: Array<{ id: Tab; label: string; statuses: LeadStatus[] | null }> = [
  { id: 'review', label: 'To review', statuses: ['new', 'request_failed'] },
  { id: 'requested', label: 'Approved', statuses: ['approved', 'requested'] },
  { id: 'rejected', label: 'Rejected', statuses: ['rejected'] },
  { id: 'all', label: 'All', statuses: null },
];

const STATUS_STYLE: Record<LeadStatus, { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-white/10 text-ink-100' },
  approved: { label: 'Requesting…', className: 'bg-warn-500/15 text-warn-500' },
  requested: { label: 'IM requested', className: 'bg-positive-500/15 text-positive-500' },
  request_failed: { label: 'Request failed', className: 'bg-negative-500/15 text-negative-500' },
  rejected: { label: 'Rejected', className: 'bg-white/5 text-ink-300' },
};

function money(value: string | null, currency: string | null): string {
  if (value === null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency ?? 'GBP',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n);
}

function shortDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function daysUntil(date: string): number {
  return Math.ceil((new Date(date).getTime() - Date.now()) / 86_400_000);
}

function ScanStatus({ scans, onScan, scanning }: { scans: LeadScan[]; onScan: () => void; scanning: boolean }) {
  const { isAdmin } = useSession();
  const last = scans.find((s) => s.mode === 'scan');
  let text = 'The scanner has not run yet.';
  if (last?.status === 'running') text = `Scanning since ${new Date(last.started_at).toLocaleString('en-GB')}…`;
  else if (last?.status === 'ok')
    text = `Last scan ${new Date(last.started_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}: ${last.leads_found} new lead${last.leads_found === 1 ? '' : 's'}.`;
  else if (last?.status === 'failed') text = `Last scan failed: ${last.error ?? 'unknown error'}`;
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm text-ink-300">
      <span>{text} Runs every 3 days.</span>
      {isAdmin ? (
      <button
        onClick={onScan}
        disabled={scanning || last?.status === 'running'}
        className="px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-ink-100 disabled:opacity-50"
      >
        {scanning ? 'Starting…' : 'Scan now'}
      </button>
      ) : null}
    </div>
  );
}

function LeadCard({
  lead,
  busy,
  onDecide,
}: {
  lead: Lead;
  busy: boolean;
  onDecide: (lead: Lead, status: 'approved' | 'rejected') => void;
}) {
  const style = STATUS_STYLE[lead.status];
  const automatic = AUTOMATABLE.includes(lead.request_mechanism);
  // Approving a lead sends an enquiry as Wakeflow, so it stays with admins.
  const { isAdmin } = useSession();
  const canDecide = isAdmin && (lead.status === 'new' || lead.status === 'request_failed');
  const deadlineDays = lead.deadline ? daysUntil(lead.deadline) : null;

  return (
    <article className="card p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="font-semibold leading-snug">
            {lead.url ? (
              <a href={lead.url} target="_blank" rel="noreferrer" className="hover:underline">
                {lead.title}
              </a>
            ) : (
              lead.title
            )}
          </h2>
          <p className="text-xs text-ink-300 mt-0.5">
            {lead.source} · ref {lead.source_ref}
            {lead.country ? ` · ${lead.country}` : ''} · found {shortDate(lead.found_at)}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {lead.verdict === 'near_miss' && <span className="pill bg-warn-500/15 text-warn-500">Near miss</span>}
          <span className={`pill ${style.className}`}>{style.label}</span>
        </div>
      </div>

      <dl className="grid grid-cols-3 gap-2 text-sm">
        <div>
          <dt className="kpi-label">Revenue</dt>
          <dd className="tabular-nums">{money(lead.revenue, lead.currency)}</dd>
        </div>
        <div>
          <dt className="kpi-label">EBITDA</dt>
          <dd className="tabular-nums">{money(lead.ebitda, lead.currency)}</dd>
        </div>
        <div>
          <dt className="kpi-label">Asking</dt>
          <dd className="tabular-nums">{money(lead.asking_price, lead.currency)}</dd>
        </div>
      </dl>

      {lead.summary && <p className="text-sm text-ink-100/90">{lead.summary}</p>}

      {(lead.flags.length > 0 || lead.possible_duplicate || deadlineDays !== null) && (
        <ul className="flex flex-wrap gap-1.5">
          {deadlineDays !== null && (
            <li className={`pill ${deadlineDays <= 3 ? 'bg-negative-500/15 text-negative-500' : 'bg-warn-500/15 text-warn-500'}`}>
              Offers by {shortDate(lead.deadline)} ({deadlineDays < 0 ? 'passed' : `${deadlineDays}d`})
            </li>
          )}
          {lead.possible_duplicate && (
            <li className="pill bg-white/5 text-ink-300">Possible duplicate: {lead.possible_duplicate}</li>
          )}
          {lead.flags.map((f) => (
            <li key={f} className="pill bg-white/5 text-ink-300">
              {f}
            </li>
          ))}
        </ul>
      )}

      {lead.chase_flag && (
        <p className="pill bg-warn-500/15 text-warn-500">Needs you: {lead.chase_flag}</p>
      )}

      {lead.request_result && (
        <p className={`whitespace-pre-line text-xs ${lead.status === 'request_failed' ? 'text-negative-500' : 'text-ink-300'}`}>
          {lead.request_result}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/5 pt-3">
        <span className="text-xs text-ink-300">
          {MECHANISM_LABELS[lead.request_mechanism]}
          {!automatic && ' · you enquire yourself'}
          {lead.contact_email ? ` · ${lead.contact_email}` : ''}
        </span>
        {canDecide && (
          <div className="flex gap-2">
            <button
              onClick={() => onDecide(lead, 'rejected')}
              disabled={busy}
              className="px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-sm disabled:opacity-50"
            >
              Reject
            </button>
            <button
              onClick={() => onDecide(lead, 'approved')}
              disabled={busy}
              title={
                automatic
                  ? 'Requests the IM now (signs the NDA / submits the enquiry as Wakeflow)'
                  : 'Marks it approved; this source needs you to enquire by hand'
              }
              className="px-3 py-1.5 rounded-md bg-positive-500/90 hover:bg-positive-500 text-ink-900 text-sm font-medium disabled:opacity-50"
            >
              {lead.status === 'request_failed' ? 'Retry request' : automatic ? 'Approve & request IM' : 'Approve'}
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

export function LeadsView() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [scans, setScans] = useState<LeadScan[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('review');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  const handle = useCallback((e: unknown) => {
    if (e instanceof AuthExpiredError) {
      window.location.reload();
      return;
    }
    setError((e as Error).message);
  }, []);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([listLeads(), listLeadScans()]);
      setLeads(l);
      setScans(s);
      setState('ready');
    } catch (e) {
      handle(e);
      setState('error');
    }
  }, [handle]);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(() => {
    const c: Record<Tab, number> = { review: 0, requested: 0, rejected: 0, all: leads.length };
    for (const t of TABS) if (t.statuses) c[t.id] = leads.filter((l) => t.statuses!.includes(l.status)).length;
    return c;
  }, [leads]);

  const shown = useMemo(() => {
    const statuses = TABS.find((t) => t.id === tab)?.statuses;
    const list = statuses ? leads.filter((l) => statuses.includes(l.status)) : leads;
    // Deadlines first (distressed sales expire in days), then matches before near-misses.
    return [...list].sort((a, b) => {
      if (a.deadline && !b.deadline) return -1;
      if (!a.deadline && b.deadline) return 1;
      if (a.deadline && b.deadline) return a.deadline.localeCompare(b.deadline);
      if (a.verdict !== b.verdict) return a.verdict === 'match' ? -1 : 1;
      return b.found_at.localeCompare(a.found_at);
    });
  }, [leads, tab]);

  const decide = async (lead: Lead, status: 'approved' | 'rejected') => {
    setBusyId(lead.id);
    setError(null);
    try {
      const updated = await decideLead(lead.id, status);
      setLeads((all) => all.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
    } catch (e) {
      handle(e);
    } finally {
      setBusyId(null);
    }
  };

  const scanNow = async () => {
    setScanning(true);
    setError(null);
    try {
      await startScan();
      await load();
    } catch (e) {
      handle(e);
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="flex max-w-6xl flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Leads</h1>
          <p className="mt-1 text-sm text-ink-400">
            Listings the scanner found that match the criteria. New leads are accepted
            automatically and their IMs requested; anything that couldn't be requested waits
            here for you.
          </p>
        </div>
        <ScanStatus scans={scans} onScan={() => void scanNow()} scanning={scanning} />
      </div>

      <nav className="flex w-fit gap-0.5 rounded-xl border border-white/[0.06] bg-ink-800/60 p-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              tab === t.id
                ? 'bg-white/10 text-ink-100'
                : 'text-ink-400 hover:bg-white/[0.04] hover:text-ink-100'
            }`}
          >
            {t.label} <span className="ml-0.5 tabular-nums text-ink-500">{counts[t.id]}</span>
          </button>
        ))}
      </nav>

      {error && <div className="card p-3 text-sm text-negative-500">{error}</div>}

      {state === 'loading' && <div className="card p-8 text-center text-ink-300 text-sm">Loading…</div>}
      {state === 'ready' && shown.length === 0 && (
        <div className="card p-8 text-center text-ink-300 text-sm">
          {tab === 'review' ? 'Nothing waiting for review.' : 'No leads here.'}
        </div>
      )}
      {state === 'ready' && shown.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {shown.map((lead) => (
            <LeadCard key={lead.id} lead={lead} busy={busyId === lead.id} onDecide={(l, s) => void decide(l, s)} />
          ))}
        </div>
      )}
    </div>
  );
}
